import {ChangeEvent, KeyboardEvent, FC, useMemo} from 'react';
//formik
import {FieldInputProps, FormikProps} from 'formik';
//mui
import {Box, BaseTextFieldProps, TextField as MuiInput, InputAdornmentProps, SxProps, Theme} from '@mui/material';
//constants
import {TEXT} from '@/utils/constants';
//helpers
import {getValidationErrorsFromArray, getValidationErrorsFromObject} from '@/utils/helpers';
//components
import InputLabel from '../InputLabel';
import FormHelperText from '../../FormHelperText';
//styles
import {inputStyle} from './Input.styles';

interface ExtendedInputProps extends BaseTextFieldProps {
	field?: FieldInputProps<string | number>;
	form?: FormikProps<any>;
	// label?: string;
	label?: React.ReactNode;
	disabled?: boolean;
	disableErrorMessage?: boolean;
	parentClassName?: SxProps<Theme>;
	isArrayData?: boolean;
	isObjectData?: boolean;
	InputProps?: {
		startAdornment?: React.ReactElement<InputAdornmentProps, string>;
	};
	onChange?: (event: string) => void;
	handlePressEnter?: () => void;
	errorMessage?: string;
}

const Input: FC<ExtendedInputProps> = ({
	field,
	form,
	label,
	disabled,
	disableErrorMessage,
	parentClassName,
	id,
	type,
	InputProps,
	isArrayData,
	isObjectData,
	onChange,
	handlePressEnter,
	errorMessage,
	...rest
}) => {
	const errorText = useMemo(() => {
		if (field && form) {
			if (isArrayData) return getValidationErrorsFromArray(field, form);
			if (isObjectData) return getValidationErrorsFromObject(field, form);
			return (form.errors[field.name] && form.touched[field.name] && (form.errors[field.name] as string)) || undefined;
		}
	}, [field, form]);

	const changeHandler = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (onChange) {
			const {value} = event.target;
			onChange(value);
		} else {
			field?.onChange(event);
		}
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter' && handlePressEnter) {
			handlePressEnter();
		}
	};

	return (
		<Box sx={{...inputStyle.wrapper, ...parentClassName}}>
			{label && <InputLabel htmlFor={id} error={!!errorText} label={label} disabled={disabled} />}

			<MuiInput
				{...field}
				{...rest}
				onKeyDown={handleKeyDown}
				id={id}
				onChange={changeHandler}
				onBlur={(e) => {
					field?.onBlur(e);
					rest.onBlur?.(e);
				}}
				InputProps={{...InputProps}}
				type={type || TEXT}
				error={!!errorText}
				disabled={disabled}
			/>

			{!disableErrorMessage && errorText && <FormHelperText error title={errorText} />}
			{errorMessage && <FormHelperText error title={errorMessage} />}
		</Box>
	);
};

export default Input;
